import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

export default function Hero() {
  const canvasRef = useRef(null)
  const autoWordRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const gridSize = 50
    let hoveredCells = new Map()
    let animationId

    function resizeCanvas() {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }

    function drawGrid() {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Grille de base
      // Le canvas ne lit pas les variables CSS : couleur de bordure du thème courant.
      ctx.strokeStyle = `rgb(${getComputedStyle(document.documentElement).getPropertyValue('--rgb-border').trim() || '38 38 38'})`
      ctx.lineWidth = 1

      for (let x = 0; x <= canvas.width; x += gridSize) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }

      for (let y = 0; y <= canvas.height; y += gridSize) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }

      // Cellules survolées (couleur de marque du thème courant)
      const brand = getComputedStyle(document.documentElement).getPropertyValue('--rgb-brand').trim() || '134 83 231'
      const now = Date.now()
      hoveredCells.forEach((timestamp, key) => {
        const [x, y] = key.split(',').map(Number)
        const age = now - timestamp
        const fadeTime = 1500

        if (age < fadeTime) {
          const opacity = 1 - (age / fadeTime)
          ctx.fillStyle = `rgb(${brand} / ${opacity * 0.3})`
          ctx.fillRect(x * gridSize + 1, y * gridSize + 1, gridSize - 2, gridSize - 2)

          ctx.strokeStyle = `rgb(${brand} / ${opacity * 0.8})`
          ctx.lineWidth = 2
          ctx.strokeRect(x * gridSize + 1, y * gridSize + 1, gridSize - 2, gridSize - 2)
        } else {
          hoveredCells.delete(key)
        }
      })

      animationId = requestAnimationFrame(drawGrid)
    }

    function handleMouseMove(e) {
      const rect = canvas.getBoundingClientRect()
      const x = Math.floor((e.clientX - rect.left) / gridSize)
      const y = Math.floor((e.clientY - rect.top) / gridSize)
      const key = `${x},${y}`

      if (!hoveredCells.has(key)) {
        hoveredCells.set(key, Date.now())
      }
    }

    canvas.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('resize', resizeCanvas)

    resizeCanvas()
    drawGrid()

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', resizeCanvas)
      cancelAnimationFrame(animationId)
    }
  }, [])

  // Animation "automatiquement"
  useEffect(() => {
    const autoWord = autoWordRef.current
    if (!autoWord) return

    const text = autoWord.textContent
    autoWord.innerHTML = text
      .split('')
      .map((char, i) => `<span class="auto-letter" style="--letter-index: ${i}">${char}</span>`)
      .join('')

    function triggerWave() {
      const letters = autoWord.querySelectorAll('.auto-letter')
      letters.forEach((letter, index) => {
        setTimeout(() => {
          letter.classList.add('glow')
          setTimeout(() => letter.classList.remove('glow'), 600)
        }, index * 80)
      })
    }

    const interval = setInterval(triggerWave, 7000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div id="hero" className="hero-section">
      <div className="hero-bg">
        <canvas className="hero-grid-canvas" ref={canvasRef}></canvas>
        <div className="hero-glow"></div>
      </div>
      <div className="container">
        <div className="hero-content">
          <div className="hero-badge">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L10.5 6L16 7L12 11L13 16L8 13.5L3 16L4 11L0 7L5.5 6L8 1Z" fill="currentColor" />
            </svg>
            <span>X entreprises nous font confiance</span>
          </div>

          <h1 className="hero-title">
            La plateforme qui trouve, contacte et convertit vos prospects{' '}
            <span className="text-gradient" ref={autoWordRef}>
              automatiquement
            </span>
          </h1>

          <p className="hero-description">
            Unifiez votre génération de leads, vos campagnes d'emails et vos séquences multicanales dans une interface simple, alimentée par nos supergent IA.
          </p>

          <div className="hero-cta">
            <Link to="/register" className="btn btn-primary btn-lg">
              Essayer gratuitement
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <button className="btn btn-secondary btn-lg">Voir une démonstration</button>
          </div>

          <div className="hero-stats">
            <div className="stat">
              <div className="stat-number">XM+</div>
              <div className="stat-label">Emails envoyés</div>
            </div>
            <div className="stat">
              <div className="stat-number">X%</div>
              <div className="stat-label">Taux d'ouverture</div>
            </div>
            <div className="stat">
              <div className="stat-number">X%</div>
              <div className="stat-label">Emails valides</div>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div className="terminal-title">jean-louis.ai</div>
            </div>
            <div className="terminal-body">
              <div className="terminal-line">
                <span className="prompt">$</span>
                <span className="command">npm run find-prospects</span>
              </div>
              <div className="terminal-output">
                {[
                  'Recherche de 50M+ profils...',
                  '247 prospects qualifiés trouvés en 3.2s',
                  'Enrichissement avec l\'IA...',
                  'Lancement de la campagne outreach...',
                ].map((text, i) => (
                  <div key={i} className={`output-line ${i === 3 ? 'typing' : ''}`} style={{ animationDelay: `${i * 0.4}s` }}>
                    <svg className={i === 3 ? 'icon-loading' : 'icon-check'} width="16" height="16" viewBox="0 0 16 16" fill="none">
                      {i === 3 ? (
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="10 10" />
                      ) : (
                        <path d="M13 4L6 11L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      )}
                    </svg>
                    {text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
